import React, { useEffect, useState } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  Clock,
  BarChart,
  Database,
  TrendingUp,
  RefreshCw,
} from 'lucide-react';

import { getSummary14d, getConversations } from '../lib/api';
import { ConversationLog, InsightResumen14d } from '../types';
import { WeeklyApiComparisonChart } from './WeeklyApiComparisonChart';
import { DualHourlyChart } from './DualHourlyChart';

interface DashboardProps {
  addNotification?: (notification: {
    type: 'success' | 'error' | 'warning' | 'info';
    title: string;
    message?: string;
    duration?: number;
    action?: {
      label: string;
      onClick: () => void;
    };
  }) => void;
}

export function Dashboard({ addNotification }: DashboardProps) {
  const [summary, setSummary] = useState<InsightResumen14d | null>(null);
  const [conversaciones, setConversaciones] = useState<ConversationLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  // Fecha seleccionada (YYYY-MM-DD)
  const today = new Date();
  const maxDate = today.toISOString().split('T')[0];
  const [selectedDate, setSelectedDate] = useState(maxDate);

  // Helpers numéricos
  const safeNumber = (num?: number, decimals = 0) =>
    typeof num === 'number' ? num.toFixed(decimals) : 'N/A';
  const safeLocaleNumber = (num?: number) =>
    typeof num === 'number' ? num.toLocaleString() : 'N/A';

  const actual = summary?.semana_actual;
  const comparacion = summary?.comparacion;
  const peticiones_api = summary?.semana_actual?.total_respuestas_api ?? 0;

  // Carga inicial
  const fetchData = () => {
    // Verificar que tenemos token antes de hacer peticiones
    const token = localStorage.getItem('auth_token');
    console.log('🔍 Verificando token en Dashboard:', token ? 'Presente' : 'Ausente');
    
    if (!token) {
      console.warn('⚠️ No hay token de autenticación, mostrando notificación');
      const errorMsg = 'No se encontró token de autenticación. Intenta iniciar sesión nuevamente.';
      setError(errorMsg);
      addNotification?.({
        type: 'error',
        title: 'Sesión no encontrada',
        message: errorMsg,
        duration: 0,
        action: {
          label: 'Iniciar sesión',
          onClick: () => {
            window.location.href = import.meta.env.BASE_URL;
          },
        },
      });
      setLoading(false);
      return;
    }
    
    console.log('✅ Token encontrado, cargando datos...');
    setLoading(true);
    setError('');
    getSummary14d()
      .then((data) => {
        console.log('📊 Datos cargados exitosamente:', data);
        setSummary(data);
      })
      .catch((e) => {
        console.error('💥 Error cargando resumen:', e);
        const errorMessage = e.message || 'Error al cargar insights';
        setError(errorMessage);
        addNotification?.({
          type: 'error',
          title: 'Error al cargar datos',
          message: errorMessage,
          duration: 5000,
        });
      })
      .finally(() => setLoading(false));
  };

  // Refresh manual (resumen + conversaciones del día seleccionado)
  const refreshData = () => {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      const errorMsg = 'No se encontró token de autenticación. Intenta iniciar sesión nuevamente.';
      setError(errorMsg);
      addNotification?.({
        type: 'error',
        title: 'Sesión no encontrada',
        message: errorMsg,
        duration: 0,
        action: {
          label: 'Iniciar sesión',
          onClick: () => {
            window.location.href = import.meta.env.BASE_URL;
          },
        },
      });
      return;
    }

    setRefreshing(true);
    Promise.all([
      getSummary14d(),
      getConversations(undefined, undefined, selectedDate),
    ])
      .then(([summaryData, conversationsData]) => {
        setSummary(summaryData);
        setConversaciones(conversationsData);
        addNotification?.({
          type: 'success',
          title: 'Datos actualizados',
          message: 'Los datos se han actualizado correctamente.',
        });
      })
      .catch((e) => {
        console.error('Error al actualizar datos:', e);
        const errorMessage = e.message || 'Error al actualizar datos';
        setError(errorMessage);
        
        addNotification?.({
          type: 'error',
          title: 'Error al actualizar',
          message: errorMessage,
        });
      })
      .finally(() => setRefreshing(false));
  };

  // Efectos
  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      console.warn('No auth token found, skipping conversations fetch');
      return;
    }
    getConversations(undefined, undefined, selectedDate)
      .then(setConversaciones)
      .catch((e) => {
        console.error('Error al obtener conversaciones:', e);
        const errorMessage = e.message || 'Error al cargar conversaciones';
        
        addNotification?.({
          type: 'warning',
          title: 'Error al cargar conversaciones',
          message: errorMessage,
        });
      });
  }, [selectedDate, addNotification]);

  // Navegación de fechas
  const moveSelectedDate = (deltaDays: number) => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + deltaDays);
    const iso = d.toISOString().split('T')[0];
    setSelectedDate(iso);
  };

  const goToday = () => setSelectedDate(maxDate);

  // Vista
  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 space-y-6 pb-[max(20px,env(safe-area-inset-bottom))]">
      {/* Header glass */}
      <header
        className={[
          'o-card relative overflow-hidden p-5 sm:p-6',
          'bg-gradient-to-b from-white/10 to-white/5',
          'border border-white/15',
        ].join(' ')}
      >
        <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
          <div className="flex-1">
            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-gradient">
              Análisis Conversacional
            </h1>
            <p className="mt-1 text-sm opacity-80">
              Actividad, tendencias y horas pico de interacción
            </p>
          </div>

            {/* Controles de fecha + refresh */}
          <div className="flex items-center gap-2 sm:gap-3">
            <button
              onClick={() => moveSelectedDate(-1)}
              className="o-btn-ghost px-2 py-2"
              aria-label="Día anterior"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div className="px-3 py-2 rounded-xl border border-white/15 bg-white/8 text-sm backdrop-blur">
              <span className="opacity-80">Fecha:</span>{' '}
              <span className="font-semibold">{selectedDate}</span>
            </div>
            <button
              onClick={() => moveSelectedDate(+1)}
              className="o-btn-ghost px-2 py-2"
              aria-label="Día siguiente"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
            <button onClick={goToday} className="o-btn-ghost px-3 py-2 text-sm">
              Hoy
            </button>

            <button
              onClick={refreshData}
              disabled={refreshing}
              className={[
                'o-btn-ghost px-3 py-2',
                refreshing ? 'opacity-70 cursor-wait' : '',
              ].join(' ')}
              aria-label="Actualizar datos"
              aria-live="polite"
            >
              <RefreshCw className={`h-5 w-5 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </header>

      {/* Estado de carga / error */}
      {loading && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" aria-live="polite">
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>
      )}

      {!!error && !loading && (
        <div className="o-card text-center py-8 border border-red-400/25 bg-red-500/10">
          <AlertCircle className="h-8 w-8 text-red-400 mx-auto mb-2" />
          <p className="text-red-300">{error}</p>
        </div>
      )}

      {/* Métricas principales */}
      {!loading && !error && actual && (
        <section className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <InfoCard
            icon={<Database className="h-5 w-5" />}
            title="Peticiones a la API"
            value={safeLocaleNumber(peticiones_api)}
            trend={comparacion?.respuestas_api?.cambio_pct}
          />
          <InfoCard
            icon={<BarChart className="h-5 w-5" />}
            title="Respuestas por usuario"
            value={safeNumber(actual.respuestas_por_usuario, 2)}
            trend={comparacion?.respuestas_por_usuario?.cambio_pct}
          />
          <InfoCard
            icon={<Clock className="h-5 w-5" />}
            title="Hora pico"
            value={actual.hora_pico ? `${actual.hora_pico}:00` : 'N/D'}
          />
        </section>
      )}

      {/* Comparación semanal por día */}
      {!loading &&
        !error &&
        summary?.semana_actual?.por_dia_user &&
        summary?.semana_anterior?.por_dia_user && (
          <section className="o-card p-4 sm:p-6">
            <h2 className="text-sm font-semibold opacity-80 mb-3">
              Comparación semanal (respuestas por día)
            </h2>
            <WeeklyApiComparisonChart
              semana_actual={summary.semana_actual.por_dia_user}
              semana_anterior={summary.semana_anterior.por_dia_user}
            />
          </section>
        )}

      {/* Comparativa por hora */}
      {!loading &&
        !error &&
        summary?.semana_actual?.por_hora &&
        summary?.semana_anterior?.por_hora && (
          <section className="o-card p-4 sm:p-6">
            <h2 className="text-sm font-semibold opacity-80 mb-3">
              Distribución por hora (actual vs anterior)
            </h2>
            <DualHourlyChart
              actual={summary.semana_actual.por_hora}
              anterior={summary.semana_anterior.por_hora}
            />
          </section>
        )}

      {/* Empty states suaves */}
      {!loading &&
        !error &&
        (!summary?.semana_actual?.por_dia_user || !summary?.semana_anterior?.por_dia_user) && (
          <EmptyNote text="Sin datos suficientes para comparación semanal." />
        )}
      {!loading &&
        !error &&
        (!summary?.semana_actual?.por_hora || !summary?.semana_anterior?.por_hora) && (
          <EmptyNote text="Sin datos suficientes para comparación por hora." />
        )}
    </div>
  );
}

/* ------- Tarjeta de métrica (glass + neon suave) ------- */
const InfoCard = ({
  icon,
  title,
  value,
  trend,
}: {
  icon: React.ReactNode;
  title: string;
  value: string;
  trend?: number;
}) => {
  const isPositive = typeof trend === 'number' ? trend > 0 : undefined;

  return (
    <div
      className={[
        'o-card p-5 text-white relative overflow-hidden',
        'bg-gradient-to-b from-white/10 to-white/5 border border-white/15',
        'hover:shadow-[0_16px_48px_rgba(0,0,0,.35)] hover:-translate-y-[2px] transition-all duration-300',
      ].join(' ')}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-white/10 border border-white/15">
            {icon}
          </div>
          <span className="text-sm opacity-80">{title}</span>
        </div>

        {typeof trend === 'number' && (
          <div
            className={[
              'flex items-center text-sm font-medium',
              isPositive ? 'text-emerald-300' : 'text-rose-300',
            ].join(' ')}
          >
            <TrendingUp
              className={`h-4 w-4 mr-1 ${isPositive ? '' : 'rotate-180'}`}
            />
            {Math.abs(trend).toFixed(0)}%
          </div>
        )}
      </div>

      <div className="text-3xl font-extrabold tracking-tight">{value}</div>
    </div>
  );
};

/* ------- Skeleton para carga inicial ------- */
const SkeletonCard = () => (
  <div className="o-card p-5 bg-white/5 border border-white/10 overflow-hidden relative">
    <div className="h-5 w-24 rounded bg-white/10 mb-4" />
    <div className="h-7 w-36 rounded bg-white/10" />
    <div className="absolute inset-0 -translate-x-full animate-[shimmer_1.2s_infinite] bg-gradient-to-r from-transparent via-white/10 to-transparent" />
    <style>{`@keyframes shimmer{100%{transform:translateX(100%);}}`}</style>
  </div>
);

/* ------- Empty note ------- */
const EmptyNote = ({ text }: { text: string }) => (
  <div className="o-card p-4 text-sm opacity-80 border border-white/15">
    {text}
  </div>
);
